import {
  Category,
  DisplaySubMetric,
  EvaluationColor,
  InsightMetric,
} from '@repo/types';
import { SetURLSearchParams } from 'react-router-dom';
import { getTopThreePrioritiesFromCategories } from 'views/Results/utils/categoriesAndMetrics';
import { getEvaluationIconFromColor } from '../../EvaluationGroupsSummary/EvaluationGroupsSummary';
import { MetricsFilter, PillOptions } from './types';

export const METRICS_FILTERS_KEY = 'filters';

export function handleSetFilter(
  filter: MetricsFilter,
  urlSearchParams: URLSearchParams,
  setUrlSearchParams: SetURLSearchParams,
) {
  const currentFilters = new Set(urlSearchParams.getAll(METRICS_FILTERS_KEY));

  if (currentFilters.has(filter)) {
    currentFilters.delete(filter);

    return setUrlSearchParams(
      {
        [METRICS_FILTERS_KEY]: Array.from(currentFilters),
      },
      { replace: true },
    );
  }

  if (filter === 'all' || filter === 'top') {
    return setUrlSearchParams(
      { [METRICS_FILTERS_KEY]: [filter] },
      { replace: true },
    );
  }

  if (currentFilters.has('all')) currentFilters.delete('all');
  if (currentFilters.has('top')) currentFilters.delete('top');

  currentFilters.add(filter);

  return setUrlSearchParams(
    {
      [METRICS_FILTERS_KEY]: Array.from(currentFilters),
    },
    { replace: true },
  );
}

export function shouldShowAllMetrics(currentFilters: MetricsFilter[]) {
  return currentFilters.includes('all') || currentFilters.length === 0;
}

export const evaluationColorToMetricFilterMap: Record<
  EvaluationColor,
  MetricsFilter
> = {
  red: 'support',
  orange: 'improve',
  grey: 'okay',
  green: 'great',
};

export function calculatePills(metrics: InsightMetric[]) {
  const calculatedPills = new Map<MetricsFilter, PillOptions>();
  let prioritiesCount = 0;

  for (const { evaluation_color, priority } of metrics) {
    const filter = evaluationColorToMetricFilterMap[evaluation_color];

    if (filter && !calculatedPills.has(filter)) {
      calculatedPills.set(filter, {
        key: filter,
        label: `${filter[0].toUpperCase()}${filter.slice(1)}`,
        iconName: getEvaluationIconFromColor(evaluation_color),
      });
    }

    if (prioritiesCount < 3 && priority != null) {
      prioritiesCount++;
    }
  }

  // Fixed Support -> Improve -> Okay -> Great order (map is declared in that order).
  const pills = Object.values(evaluationColorToMetricFilterMap)
    .filter(filter => calculatedPills.has(filter))
    .map(filter => calculatedPills.get(filter)!);

  return { pills, prioritiesCount };
}

// `top` is exclusive with the evaluation chips (which union); every category is kept.
export function filterCategories(
  categories: Category[],
  currentFilters: MetricsFilter[],
): Category[] {
  if (shouldShowAllMetrics(currentFilters)) return categories;

  let matches: (submetric: DisplaySubMetric) => boolean;

  if (currentFilters.includes('top')) {
    const topSubmetrics = new Set(
      getTopThreePrioritiesFromCategories(categories),
    );

    matches = submetric => topSubmetrics.has(submetric);
  } else {
    const allowedColors = new Set<EvaluationColor>(
      (
        Object.keys(evaluationColorToMetricFilterMap) as EvaluationColor[]
      ).filter(color =>
        currentFilters.includes(evaluationColorToMetricFilterMap[color]),
      ),
    );

    matches = submetric => allowedColors.has(submetric.evaluation_color);
  }

  // Empty categories are kept (they render an empty state) so "Jump to" stays complete.
  return categories.map(category => ({
    ...category,
    orderedMetrics: category.orderedMetrics
      .map(metric => ({
        ...metric,
        orderedSubmetrics: metric.orderedSubmetrics.filter(matches),
      }))
      .filter(metric => metric.orderedSubmetrics.length > 0),
  }));
}
